import React from 'react';
import { cn } from '../../utils/cn';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  mono?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  rightAction?: React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      mono = false,
      size = 'md',
      leftIcon,
      rightIcon,
      rightAction,
      className = '',
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id || generatedId;

    const sizeStyles = {
      xs: 'text-xs py-1 px-2.5 rounded',
      sm: 'text-xs py-1.5 px-2.5 rounded-md',
      md: 'text-xs py-2 px-3 rounded-md',
      lg: 'text-sm py-2.5 px-3.5 rounded-md',
    };

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-xs font-medium text-cf-textSecondary mb-1"
          >
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          {leftIcon && (
            <div className="absolute left-2.5 flex items-center pointer-events-none text-cf-textMuted shrink-0">
              {leftIcon}
            </div>
          )}
          <input
            id={inputId}
            ref={ref}
            disabled={disabled}
            className={cn(
              'w-full bg-cf-input border border-cf-border text-cf-textPrimary placeholder:text-cf-textMuted focus:outline-none focus:border-cf-orange transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
              leftIcon && 'pl-8',
              (rightIcon || rightAction) && 'pr-8',
              mono ? 'font-mono' : 'font-sans',
              sizeStyles[size],
              error && 'border-rose-500 focus:border-rose-500',
              className
            )}
            {...props}
          />
          {rightAction ? (
            <div className="absolute right-1.5 flex items-center">{rightAction}</div>
          ) : rightIcon ? (
            <div className="absolute right-2.5 flex items-center pointer-events-none text-cf-textMuted shrink-0">
              {rightIcon}
            </div>
          ) : null}
        </div>
        {error ? (
          <span className="block text-[11px] text-rose-600 dark:text-rose-400 mt-1">
            {error}
          </span>
        ) : helperText ? (
          <span className="block text-[11px] text-cf-textMuted mt-1">
            {helperText}
          </span>
        ) : null}
      </div>
    );
  }
);

Input.displayName = 'Input';
